import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../../hooks/redux';
import { Notification } from '../../../../../hooks/useNotification';
import { TypeError } from '../../../../../types/enums';
import {
  IFreeSlotItem,
  IMatchScheduleItem,
  ITournamentSchedule,
} from '../../../../../types/schedule';
import type { ScheduleGridDayInput } from '../../../../../features/schedule/scheduleGridSelectors';
import type { IDaySchedule } from '../../../../../types/schedule';
import {
  addUnscheduledItemToSchedule,
  swapScheduleItems,
  swapPendingUnscheduledWithScheduled,
} from '../../../../../features/schedule/scheduleActions';
import { flattenScheduledMatches } from '../../../../../features/schedule/scheduleOverlap';
import {
  createPlacementInFlightGate,
  getNextFocusedSlot,
  isSlotValidInContext,
  seedFocusedPlacementSlot,
  seedFocusedPlacementSlotForScheduledMatch,
  slotsEqual,
  type PlacementKeyboardDirection,
} from '../DnDSchedule/schedulePlacementKeyboard';
import {
  buildSwapKeyboardContext,
  getNextFocusedSwapTarget,
  seedFocusedSwapTarget,
  type SwapSeedAnchor,
} from '../DnDSchedule/schedulePlacementSwapKeyboard';
import { scrollPlacementSlotIntoViewIfNeeded } from '../DnDSchedule/SchedulePlacementOverlay';
import {
  applyBlockPlacementFilter,
  buildPlacementSessionIndexBase,
  placementKeyboardContextFromSessionIndex,
  rescalePlacementSessionBandsPx,
  resolveBlockFilterDayKeys,
  type PlacementSessionIndex,
} from '../DnDSchedule/schedulePlacementIndex';
import { hasScheduleOccupancyFilters, type FilterItem } from '../schedule.helpers';
import type { IScheduleConstraints } from '../../../../../types/schedule';
import type { Class } from '../../../../../types/tournament';
import { isScheduleHistoryKeyTarget } from './scheduleLayoutHooks';
import { useScheduleZoom } from './ScheduleZoomContext';
import { isSwapTargetBlockedByConflict } from '../DnDSchedule/scheduleGridSwapPreview';
import { getPlacementConflictIndex } from '../../../../../features/schedule/placementConflictIndexCache';
import { canCommitClickPlacement } from '../../../../../features/schedule/scheduleDragDropValidation';
import type { PlacementConflictIndex } from '../../../../../features/schedule/schedulePlacementConflict';

export type ScheduleClickPlacement = {
  matchId: number;
  source: 'unscheduled' | 'scheduled_grid';
};

export type ScheduleDragPlacement = {
  match: IMatchScheduleItem;
  source: 'unscheduled' | 'scheduled';
  /** When dragging a bulk-selected scheduled anchor, all ids in the group move together. */
  groupMatchIds?: number[];
};

export type SchedulePlacementController = {
  keyboardModifierHeld: boolean;
  shiftModifierHeld: boolean;
  clickPlacement: ScheduleClickPlacement | null;
  setClickPlacement: (value: ScheduleClickPlacement | null) => void;
  dragPlacement: ScheduleDragPlacement | null;
  setDragPlacement: (value: ScheduleDragPlacement | null) => void;
  pendingPlaceMatchId: number | null;
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null;
  placementBannerDismissed: boolean;
  placementFloatRef: RefObject<HTMLDivElement>;
  scheduleGridWheelRef: RefObject<HTMLElement>;
  handlePlacementBannerPointerDownCapture: (e: React.PointerEvent<HTMLDivElement>) => void;
  pendingPlaceMatch: IMatchScheduleItem | undefined;
  activePlacementMatch: IMatchScheduleItem | null;
  focusedPlacementSlot: IFreeSlotItem | null;
  focusedSwapTargetMatchId: number | null;
  swapModifierHeld: boolean;
  placementSessionReady: boolean;
  placementSessionIndex: PlacementSessionIndex | null;
  handlePlaceAtFreeSlot: (freeSlot: IFreeSlotItem) => Promise<void>;
  handleSwapPendingWithScheduled: (targetMatch: IMatchScheduleItem) => Promise<void>;
  handleSelectUnscheduledForPlacement: (matchId: number | null) => void;
  handleSelectGridMatchForPlacement: (matchId: number) => void;
  resolveMatchFromDnDData: (data: unknown) => IMatchScheduleItem | undefined;
};

type UseSchedulePlacementStateParams = {
  schedule: ITournamentSchedule | null;
  displayView: ITournamentSchedule | null;
  displayViewStructureKey: string;
  placementIndexDays: IDaySchedule[];
  gridInput: ScheduleGridDayInput | null;
  scheduleConstraints: IScheduleConstraints | null | undefined;
  currentFilters: FilterItem[];
  unscheduledMatches: IMatchScheduleItem[];
  scheduleMutationBlocked: boolean;
  blockInvalidPlacement: boolean;
  scheduleTimeZone: string;
  classIdToObject: Record<string, Class>;
};

export function useSchedulePlacementState({
  schedule,
  displayView,
  displayViewStructureKey,
  placementIndexDays,
  gridInput,
  scheduleConstraints,
  currentFilters,
  unscheduledMatches,
  scheduleMutationBlocked,
  blockInvalidPlacement,
  scheduleTimeZone,
  classIdToObject,
}: UseSchedulePlacementStateParams): SchedulePlacementController {
  const { scale: scheduleItemScaleMultiplier, isGesturing } = useScheduleZoom();
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const dispatch = useAppDispatch();

  const [keyboardModifierHeld, setKeyboardModifierHeld] = useState(false);
  const [shiftModifierHeld, setShiftModifierHeld] = useState(false);

  useEffect(() => {
    const syncModifier = (e: KeyboardEvent | MouseEvent) => {
      setKeyboardModifierHeld(e.ctrlKey || e.metaKey);
      setShiftModifierHeld(e.shiftKey);
    };
    const clearModifier = () => {
      setKeyboardModifierHeld(false);
      setShiftModifierHeld(false);
    };
    window.addEventListener('keydown', syncModifier);
    window.addEventListener('keyup', syncModifier);
    window.addEventListener('blur', clearModifier);
    return () => {
      window.removeEventListener('keydown', syncModifier);
      window.removeEventListener('keyup', syncModifier);
      window.removeEventListener('blur', clearModifier);
    };
  }, []);

  const [clickPlacement, setClickPlacement] = useState<ScheduleClickPlacement | null>(null);
  const [focusedPlacementSlot, setFocusedPlacementSlot] = useState<IFreeSlotItem | null>(null);
  const focusedPlacementSlotRef = useRef<IFreeSlotItem | null>(null);
  focusedPlacementSlotRef.current = focusedPlacementSlot;
  const [focusedSwapTarget, setFocusedSwapTarget] = useState<IMatchScheduleItem | null>(null);
  const focusedSwapTargetRef = useRef<IMatchScheduleItem | null>(null);
  focusedSwapTargetRef.current = focusedSwapTarget;
  const placementInFlightGateRef = useRef(createPlacementInFlightGate());
  const placementConflictIndexRef = useRef<PlacementConflictIndex | null>(null);
  const prevFocusedPlacementDayRef = useRef<number | null>(null);
  const [placementSessionReady, setPlacementSessionReady] = useState(false);
  const [placementSessionIndexBase, setPlacementSessionIndexBase] =
    useState<PlacementSessionIndex | null>(null);
  const pendingPlaceMatchId = clickPlacement?.matchId ?? null;
  const pendingPlacementSource = clickPlacement?.source ?? null;
  const [placementBannerDismissed, setPlacementBannerDismissed] = useState(false);
  const placementFloatRef = useRef<HTMLDivElement>(null);
  const scheduleGridWheelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setPlacementBannerDismissed(false);
  }, [clickPlacement?.matchId, clickPlacement?.source]);

  const handlePlacementBannerPointerDownCapture = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('button')) return;

      setPlacementBannerDismissed(true);
      const banner = placementFloatRef.current;
      if (banner) {
        banner.style.pointerEvents = 'none';
      }

      if (clickPlacementRef.current == null) return;

      const hit = document.elementFromPoint(e.clientX, e.clientY);
      const anchor = hit?.closest('[data-schedule-place-anchor]');
      if (!anchor) return;

      anchor.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: e.clientX,
          clientY: e.clientY,
          view: window,
        })
      );
    },
    []
  );

  const [dragPlacement, setDragPlacement] = useState<ScheduleDragPlacement | null>(null);
  const [swapModifierHeld, setSwapModifierHeld] = useState(false);

  const scheduleFlatMatches = useMemo(() => flattenScheduledMatches(schedule), [schedule]);

  const scheduleMutationBlockedRef = useRef(scheduleMutationBlocked);
  scheduleMutationBlockedRef.current = scheduleMutationBlocked;
  const clickPlacementRef = useRef(clickPlacement);
  clickPlacementRef.current = clickPlacement;
  const dragPlacementRef = useRef(dragPlacement);
  dragPlacementRef.current = dragPlacement;
  const unscheduledMatchesRef = useRef(unscheduledMatches);
  unscheduledMatchesRef.current = unscheduledMatches;
  const scheduleFlatMatchesRef = useRef(scheduleFlatMatches);
  scheduleFlatMatchesRef.current = scheduleFlatMatches;

  const resolveMatchFromDnDData = useCallback((data: unknown): IMatchScheduleItem | undefined => {
    if (typeof data !== 'object' || data == null || !('id' in data)) return undefined;
    const id = Number((data as { id: unknown }).id);
    if (!Number.isFinite(id)) return undefined;
    return (
      scheduleFlatMatchesRef.current.find((m) => m.id === id) ??
      unscheduledMatchesRef.current.find((m) => m.id === id)
    );
  }, []);

  const pendingPlaceMatch = useMemo(() => {
    if (clickPlacement == null) return undefined;
    if (clickPlacement.source === 'unscheduled') {
      return unscheduledMatches.find((m) => m.id === clickPlacement.matchId);
    }
    return scheduleFlatMatches.find((m) => m.id === clickPlacement.matchId);
  }, [clickPlacement, unscheduledMatches, scheduleFlatMatches]);

  useEffect(() => {
    if (placementBannerDismissed) return;
    const banner = placementFloatRef.current;
    if (banner) {
      banner.style.pointerEvents = '';
    }
  }, [placementBannerDismissed, pendingPlaceMatch]);

  const activePlacementMatch = useMemo(
    () => dragPlacement?.match ?? pendingPlaceMatch ?? null,
    [dragPlacement, pendingPlaceMatch]
  );

  const placementSessionKey = useMemo(
    () =>
      clickPlacement != null
        ? `${clickPlacement.matchId}:click:${clickPlacement.source}`
        : dragPlacement != null
          ? `${dragPlacement.match.id}:drag:${dragPlacement.source}`
          : null,
    [clickPlacement, dragPlacement]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setClickPlacement(null);
        setFocusedPlacementSlot(null);
        setFocusedSwapTarget(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const placementConflictParams = useMemo(() => {
    if (!activePlacementMatch || !schedule || !scheduleConstraints) return null;
    return {
      pending: activePlacementMatch,
      schedule,
      constraints: scheduleConstraints,
      timeZone: scheduleTimeZone,
      classIdToObject,
    };
  }, [
    activePlacementMatch,
    schedule,
    scheduleConstraints,
    scheduleTimeZone,
    classIdToObject,
  ]);

  const focusedPlacementDayKey = focusedPlacementSlot?.day ?? null;

  const blockFilterDayKeys = useMemo(() => {
    if (!activePlacementMatch || !scheduleConstraints) return null;
    return resolveBlockFilterDayKeys({
      sessionDayKeys: placementIndexDays.map((d) => d.dayKey),
      singleGridDayKey: gridInput?.day?.dayKey ?? null,
      focusedPlacementSlot:
        focusedPlacementDayKey != null
          ? ({ day: focusedPlacementDayKey } as IFreeSlotItem)
          : null,
      activeMatch: activePlacementMatch,
      scheduleConstraints,
      timeZone: scheduleTimeZone,
      classIdToObject,
    });
  }, [
    activePlacementMatch,
    scheduleConstraints,
    placementIndexDays,
    gridInput?.day?.dayKey,
    focusedPlacementDayKey,
    scheduleTimeZone,
    classIdToObject,
  ]);

  const [placementSessionIndex, setPlacementSessionIndex] = useState<PlacementSessionIndex | null>(
    null
  );
  const placementSessionIndexRef = useRef<PlacementSessionIndex | null>(null);
  placementSessionIndexRef.current = placementSessionIndex;

  useEffect(() => {
    if (!placementSessionIndexBase) {
      setPlacementSessionIndex((prev) => (prev === null ? prev : null));
      placementConflictIndexRef.current = null;
      return;
    }
    if (
      !blockInvalidPlacement ||
      !scheduleConstraints ||
      !activePlacementMatch ||
      !placementConflictParams
    ) {
      setPlacementSessionIndex(placementSessionIndexBase);
      placementConflictIndexRef.current = null;
      return;
    }

    const dayKey = focusedPlacementDayKey;
    const prevDay = prevFocusedPlacementDayRef.current;
    if (dayKey != null) {
      prevFocusedPlacementDayRef.current = dayKey;
    }
    if (dayKey == null || prevDay == null || prevDay === dayKey) {
      return;
    }

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      startTransition(() => {
        if (cancelled) return;
        if (process.env.NODE_ENV === 'development') {
          performance.mark('schedule-block-filter-start');
        }
        const { sessionIndex: filtered, conflictIndex } = applyBlockPlacementFilter(
          placementSessionIndexBase,
          {
            blockInvalidPlacement: true,
            placementConflictParams,
            scheduleConstraints,
            activeMatch: activePlacementMatch,
            scale: scheduleItemScaleMultiplier,
            unscheduledMatches,
            filterDayKeys: blockFilterDayKeys,
            mergeFrom: placementSessionIndexRef.current,
          }
        );
        if (process.env.NODE_ENV === 'development') {
          performance.mark('schedule-block-filter-end');
          performance.measure(
            'schedule-block-filter',
            'schedule-block-filter-start',
            'schedule-block-filter-end'
          );
        }
        placementConflictIndexRef.current = conflictIndex;
        setPlacementSessionIndex(filtered);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [
    placementSessionIndexBase,
    blockInvalidPlacement,
    placementConflictParams,
    scheduleConstraints,
    activePlacementMatch,
    unscheduledMatches,
    blockFilterDayKeys,
    focusedPlacementDayKey,
  ]);

  const prevPlacementScaleRef = useRef(scheduleItemScaleMultiplier);

  useEffect(() => {
    prevPlacementScaleRef.current = scheduleItemScaleMultiplier;
  }, [placementSessionIndexBase]);

  useEffect(() => {
    const sessionMatch = activePlacementMatch;
    if (!placementSessionKey || !scheduleConstraints || !sessionMatch) {
      setPlacementSessionIndexBase((prev) => (prev === null ? prev : null));
      setPlacementSessionReady((prev) => (prev ? false : prev));
      return;
    }

    const daysToBuild = placementIndexDays;
    if (daysToBuild.length === 0) {
      setPlacementSessionIndexBase((prev) => (prev === null ? prev : null));
      setPlacementSessionReady((prev) => (prev ? false : prev));
      return;
    }

    let cancelled = false;
    const occupancySchedule = hasScheduleOccupancyFilters(currentFilters) ? schedule : undefined;
    const clickForSeed = clickPlacement;

    const frameId = requestAnimationFrame(() => {
      startTransition(() => {
        if (cancelled) return;
        if (process.env.NODE_ENV === 'development') {
          performance.mark('schedule-select-index-start');
        }
        const sessionIndex = buildPlacementSessionIndexBase({
          days: daysToBuild,
          scheduleConstraints,
          activeMatch: sessionMatch,
          scale: scheduleItemScaleMultiplier,
          timeZone: scheduleTimeZone,
          occupancySchedule,
        });
        const seedDayKeys = resolveBlockFilterDayKeys({
          sessionDayKeys: daysToBuild.map((d) => d.dayKey),
          singleGridDayKey: gridInput?.day?.dayKey ?? null,
          focusedPlacementSlot: null,
          activeMatch: sessionMatch,
          scheduleConstraints,
          timeZone: scheduleTimeZone,
          classIdToObject,
        });
        let publishedIndex = sessionIndex;
        if (blockInvalidPlacement && placementConflictParams) {
          if (process.env.NODE_ENV === 'development') {
            performance.mark('schedule-block-filter-start');
          }
          const filterResult = applyBlockPlacementFilter(sessionIndex, {
            blockInvalidPlacement: true,
            placementConflictParams,
            scheduleConstraints,
            activeMatch: sessionMatch,
            scale: scheduleItemScaleMultiplier,
            unscheduledMatches,
            filterDayKeys: seedDayKeys,
          });
          publishedIndex = filterResult.sessionIndex;
          placementConflictIndexRef.current = filterResult.conflictIndex;
          if (process.env.NODE_ENV === 'development') {
            performance.mark('schedule-block-filter-end');
            performance.measure(
              'schedule-block-filter',
              'schedule-block-filter-start',
              'schedule-block-filter-end'
            );
          }
        } else {
          placementConflictIndexRef.current = null;
        }
        if (process.env.NODE_ENV === 'development') {
          performance.mark('schedule-select-index-end');
        }
        setPlacementSessionIndexBase(sessionIndex);
        setPlacementSessionIndex(publishedIndex);
        setPlacementSessionReady(true);
        prevFocusedPlacementDayRef.current = null;
        if (clickForSeed != null && dragPlacementRef.current == null) {
          const context = placementKeyboardContextFromSessionIndex(publishedIndex);
          const seed =
            clickForSeed.source === 'scheduled_grid'
              ? seedFocusedPlacementSlotForScheduledMatch(context, sessionMatch)
              : seedFocusedPlacementSlot(context);
          prevFocusedPlacementDayRef.current = seed?.day ?? null;
          setFocusedPlacementSlot(seed);
        }
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [
    placementSessionKey,
    gridInput?.courtRefsKey,
    gridInput?.day,
    displayViewStructureKey,
    placementIndexDays,
    scheduleConstraints,
    activePlacementMatch,
    currentFilters,
    schedule,
    scheduleTimeZone,
    classIdToObject,
    blockInvalidPlacement,
    unscheduledMatches,
    placementConflictParams,
  ]);

  useEffect(() => {
    const base = placementSessionIndexBase;
    const match = activePlacementMatch;
    if (!base || !match || !scheduleConstraints) return;
    if (isGesturing) return;

    if (prevPlacementScaleRef.current === scheduleItemScaleMultiplier) return;
    prevPlacementScaleRef.current = scheduleItemScaleMultiplier;

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      startTransition(() => {
        if (cancelled) return;
        if (process.env.NODE_ENV === 'development') {
          performance.mark('schedule-zoom-rescale-bands-start');
        }
        const source = placementSessionIndexRef.current ?? base;
        const rescaled = rescalePlacementSessionBandsPx(
          source,
          scheduleItemScaleMultiplier,
          scheduleConstraints,
          match
        );
        if (process.env.NODE_ENV === 'development') {
          performance.mark('schedule-zoom-rescale-bands-end');
          performance.measure(
            'schedule-zoom-rescale-bands',
            'schedule-zoom-rescale-bands-start',
            'schedule-zoom-rescale-bands-end'
          );
        }
        setPlacementSessionIndex(rescaled);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [
    isGesturing,
    scheduleItemScaleMultiplier,
    placementSessionIndexBase,
    scheduleConstraints,
    activePlacementMatch,
  ]);

  const placementKeyboardContext = useMemo(() => {
    if (!placementSessionReady || !placementSessionIndex) return null;
    return placementKeyboardContextFromSessionIndex(placementSessionIndex);
  }, [placementSessionReady, placementSessionIndex]);

  const swapKeyboardContext = useMemo(() => {
    if (!displayView || !scheduleConstraints || !pendingPlaceMatch || clickPlacement == null) {
      return null;
    }
    return buildSwapKeyboardContext(
      displayView,
      pendingPlaceMatch,
      scheduleFlatMatches,
      scheduleConstraints,
      pendingPlacementSource
    );
  }, [
    displayView,
    scheduleConstraints,
    pendingPlaceMatch,
    clickPlacement,
    scheduleFlatMatches,
    pendingPlacementSource,
  ]);

  useEffect(() => {
    if (clickPlacement == null || dragPlacement != null) {
      setFocusedPlacementSlot(null);
      setFocusedSwapTarget(null);
    }
  }, [placementSessionKey, clickPlacement, dragPlacement]);

  useEffect(() => {
    if (!focusedPlacementSlot || clickPlacement == null || dragPlacement != null) return;
    if (!placementSessionReady) return;
    scrollPlacementSlotIntoViewIfNeeded(focusedPlacementSlot);
  }, [placementSessionKey, clickPlacement, dragPlacement, focusedPlacementSlot, placementSessionReady]);

  const scrollFocusedSwapTargetIntoView = useCallback((matchId: number) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-schedule-swap-focus="${matchId}"]`);
      el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }, []);

  useEffect(() => {
    if (clickPlacement == null && dragPlacement == null) {
      setSwapModifierHeld(false);
      return;
    }
    const syncModifier = (e: KeyboardEvent | MouseEvent) => {
      const held = e.ctrlKey || e.metaKey;
      setSwapModifierHeld(held);
      if (!held && e.type === 'keyup') {
        setFocusedSwapTarget(null);
      }
    };
    const clearModifier = () => {
      setSwapModifierHeld(false);
      setFocusedSwapTarget(null);
    };
    window.addEventListener('keydown', syncModifier);
    window.addEventListener('keyup', syncModifier);
    window.addEventListener('blur', clearModifier);
    return () => {
      window.removeEventListener('keydown', syncModifier);
      window.removeEventListener('keyup', syncModifier);
      window.removeEventListener('blur', clearModifier);
    };
  }, [clickPlacement, dragPlacement]);

  useEffect(() => {
    if (clickPlacement == null) return;
    if (clickPlacement.source === 'unscheduled') {
      if (!unscheduledMatches.some((m) => m.id === clickPlacement.matchId)) {
        setClickPlacement(null);
      }
    } else {
      const stillScheduled = scheduleFlatMatches.some(
        (m) => m.id === clickPlacement.matchId && m.scheduleDate
      );
      if (!stillScheduled) {
        setClickPlacement(null);
      }
    }
  }, [unscheduledMatches, scheduleFlatMatches, clickPlacement]);

  const notifyScheduleReadOnlyBlocked = useCallback(() => {
    Notification(TypeError.info, t(prefix + 'scheduleReadOnlyBlockedToast'), 'bottom-right');
  }, [t]);

  const handlePlaceAtFreeSlot = useCallback(async (freeSlot: IFreeSlotItem) => {
    const gate = placementInFlightGateRef.current;
    const acquired = gate.tryAcquire();
    if (!acquired) return;
    try {
      if (scheduleMutationBlockedRef.current) {
        notifyScheduleReadOnlyBlocked();
        return;
      }
      const click = clickPlacementRef.current;
      if (click == null) return;
      setFocusedPlacementSlot(freeSlot);
      const match =
        click.source === 'unscheduled'
          ? unscheduledMatchesRef.current.find((m) => m.id === click.matchId)
          : scheduleFlatMatchesRef.current.find((m) => m.id === click.matchId);
      if (!match) {
        setClickPlacement(null);
        setFocusedPlacementSlot(null);
        return;
      }
      if (
        blockInvalidPlacement &&
        scheduleConstraints &&
        schedule &&
        !canCommitClickPlacement({
          freeSlot,
          match,
          clickSource: click.source,
          schedule,
          constraints: scheduleConstraints,
          timeZone: scheduleTimeZone,
          classIdToObject,
          blockInvalidPlacement: true,
          conflictIndex: placementConflictIndexRef.current,
        })
      ) {
        return;
      }
      let ok = false;
      if (click.source === 'unscheduled') {
        ok = Boolean(await dispatch(addUnscheduledItemToSchedule(freeSlot, match, t)));
      } else {
        ok = Boolean(await dispatch(swapScheduleItems(match, freeSlot, t)));
      }
      if (ok) {
        setDragPlacement(null);
        setClickPlacement(null);
        setFocusedPlacementSlot(null);
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest('[data-schedule-place-anchor]')) {
          active.blur();
        }
      }
    } finally {
      gate.release();
    }
  }, [
    dispatch,
    t,
    notifyScheduleReadOnlyBlocked,
    blockInvalidPlacement,
    scheduleConstraints,
    schedule,
    scheduleTimeZone,
    classIdToObject,
  ]);

  const handleSwapPendingWithScheduled = useCallback(async (targetMatch: IMatchScheduleItem) => {
    if (scheduleMutationBlockedRef.current) {
      notifyScheduleReadOnlyBlocked();
      return;
    }
    const drag = dragPlacementRef.current;
    const click = clickPlacementRef.current;
    const activeMatch = drag?.match ??
      (click == null
        ? null
        : click.source === 'unscheduled'
          ? unscheduledMatchesRef.current.find((m) => m.id === click.matchId)
          : scheduleFlatMatchesRef.current.find((m) => m.id === click.matchId));
    if (blockInvalidPlacement && activeMatch && scheduleConstraints && schedule) {
      const swapConflictIndex = getPlacementConflictIndex(
        schedule,
        scheduleConstraints,
        scheduleTimeZone,
        null,
        unscheduledMatchesRef.current,
        classIdToObject
      );
      if (
        isSwapTargetBlockedByConflict({
          activePlacementMatch: activeMatch,
          scheduleConstraints,
          rangeItem: targetMatch,
          allScheduledMatchesFlat: scheduleFlatMatchesRef.current,
          pendingPlacementSource:
            drag?.source === 'scheduled' || click?.source === 'scheduled_grid'
              ? 'scheduled_grid'
              : drag?.source === 'unscheduled' || click?.source === 'unscheduled'
                ? 'unscheduled'
                : null,
          scheduleDragActive: drag != null,
          dragPlacementSource: drag?.source ?? null,
          blockInvalidPlacement: true,
          canonicalSchedule: schedule,
          scheduleTimeZone,
          classIdToObject,
          swapConflictIndex,
        })
      ) {
        return;
      }
    }
    if (drag) {
      const { match, source } = drag;
      let ok = false;
      if (source === 'unscheduled') {
        ok = Boolean(await dispatch(swapPendingUnscheduledWithScheduled(match, targetMatch, t)));
      } else if (match.id !== targetMatch.id) {
        ok = Boolean(await dispatch(swapScheduleItems(match, targetMatch, t)));
      }
      if (ok) {
        setClickPlacement(null);
        setFocusedSwapTarget(null);
      }
      return;
    }
    if (click == null) return;
    const match =
      click.source === 'unscheduled'
        ? unscheduledMatchesRef.current.find((m) => m.id === click.matchId)
        : scheduleFlatMatchesRef.current.find((m) => m.id === click.matchId);
    if (!match) {
      setClickPlacement(null);
      return;
    }
    let ok = false;
    if (click.source === 'unscheduled') {
      ok = Boolean(await dispatch(swapPendingUnscheduledWithScheduled(match, targetMatch, t)));
    } else if (match.id !== targetMatch.id) {
      ok = Boolean(await dispatch(swapScheduleItems(match, targetMatch, t)));
    }
    if (ok) {
      setClickPlacement(null);
      setFocusedSwapTarget(null);
    }
  }, [
    blockInvalidPlacement,
    classIdToObject,
    dispatch,
    schedule,
    scheduleConstraints,
    scheduleTimeZone,
    t,
    notifyScheduleReadOnlyBlocked,
  ]);

  useEffect(() => {
    if (clickPlacement == null || dragPlacement != null) return;

    const arrowToDirection = (key: string): PlacementKeyboardDirection | null => {
      if (key === 'ArrowUp') return 'up';
      if (key === 'ArrowDown') return 'down';
      if (key === 'ArrowLeft') return 'left';
      if (key === 'ArrowRight') return 'right';
      return null;
    };

    const onKey = (e: KeyboardEvent) => {
      if (isScheduleHistoryKeyTarget(e.target)) return;

      const direction = arrowToDirection(e.key);
      const isEnter = e.key === 'Enter';
      if (!direction && !isEnter) return;

      const modifier = e.ctrlKey || e.metaKey;
      if (modifier && !e.altKey) {
        const swapContext = swapKeyboardContext;
        if (!swapContext) return;

        if (isEnter) {
          const target = focusedSwapTargetRef.current;
          if (!target) return;
          e.preventDefault();
          if (scheduleMutationBlocked) {
            notifyScheduleReadOnlyBlocked();
            return;
          }
          void handleSwapPendingWithScheduled(target);
          return;
        }

        if (!direction) return;
        e.preventDefault();
        const current = focusedSwapTargetRef.current;
        if (!current) {
          const click = clickPlacementRef.current;
          const pendingMatch =
            click?.source === 'unscheduled'
              ? unscheduledMatchesRef.current.find((m) => m.id === click.matchId)
              : scheduleFlatMatchesRef.current.find((m) => m.id === click?.matchId);
          const slot = focusedPlacementSlotRef.current;
          const anchor: SwapSeedAnchor | undefined = slot
            ? {
                courtId: slot.court.id,
                startMs: new Date(slot.scheduleDate).getTime(),
              }
            : pendingMatch?.scheduleDate && pendingMatch.court
              ? {
                  courtId: pendingMatch.court.id,
                  startMs: new Date(pendingMatch.scheduleDate).getTime(),
                }
              : undefined;
          const seeded = seedFocusedSwapTarget(swapContext, anchor);
          if (seeded) {
            setFocusedSwapTarget(seeded);
            scrollFocusedSwapTargetIntoView(seeded.id);
          }
          return;
        }
        const next = getNextFocusedSwapTarget(direction, current, swapContext);
        if (next.id !== current.id) {
          setFocusedSwapTarget(next);
          scrollFocusedSwapTargetIntoView(next.id);
        }
        return;
      }

      if (e.altKey || modifier) return;

      const context = placementKeyboardContext;
      if (!context) return;

      if (isEnter) {
        const slot = focusedPlacementSlotRef.current;
        const slotValid = !!slot && isSlotValidInContext(slot, context);
        if (!slot || !slotValid) return;
        e.preventDefault();
        if (scheduleMutationBlocked) {
          notifyScheduleReadOnlyBlocked();
          return;
        }
        void handlePlaceAtFreeSlot(slot);
        return;
      }

      if (!direction) return;

      const resolveSeedSlot = (): IFreeSlotItem | null => {
        const click = clickPlacementRef.current;
        if (!click) return null;
        const match =
          click.source === 'unscheduled'
            ? unscheduledMatchesRef.current.find((m) => m.id === click.matchId)
            : scheduleFlatMatchesRef.current.find((m) => m.id === click.matchId);
        if (!match) return null;
        if (click.source === 'scheduled_grid') {
          return seedFocusedPlacementSlotForScheduledMatch(context, match);
        }
        return seedFocusedPlacementSlot(context);
      };

      let current = focusedPlacementSlotRef.current;
      if (!current) {
        current = resolveSeedSlot();
        if (!current) return;
        e.preventDefault();
        const next = getNextFocusedSlot(direction, current, context);
        const target = slotsEqual(next, current) ? current : next;
        setFocusedPlacementSlot(target);
        scrollPlacementSlotIntoViewIfNeeded(target);
        return;
      }

      e.preventDefault();
      const next = getNextFocusedSlot(direction, current, context);
      if (!slotsEqual(next, current)) {
        setFocusedPlacementSlot(next);
        scrollPlacementSlotIntoViewIfNeeded(next);
      }
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [
    clickPlacement,
    dragPlacement,
    placementKeyboardContext,
    swapKeyboardContext,
    scheduleMutationBlocked,
    notifyScheduleReadOnlyBlocked,
    handlePlaceAtFreeSlot,
    handleSwapPendingWithScheduled,
    scrollFocusedSwapTargetIntoView,
  ]);

  const handleSelectUnscheduledForPlacement = useCallback(
    (matchId: number | null) => {
      if (matchId != null && scheduleMutationBlockedRef.current) {
        notifyScheduleReadOnlyBlocked();
        return;
      }
      if (matchId == null) {
        setClickPlacement(null);
      } else {
        setClickPlacement({ matchId, source: 'unscheduled' });
      }
    },
    [notifyScheduleReadOnlyBlocked]
  );

  const handleSelectGridMatchForPlacement = useCallback(
    (matchId: number) => {
      if (scheduleMutationBlockedRef.current) {
        notifyScheduleReadOnlyBlocked();
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        performance.mark('schedule-select-click-end');
      }
      setClickPlacement({ matchId, source: 'scheduled_grid' });
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest('[data-schedule-place-anchor]')) {
          active.blur();
        }
      });
    },
    [notifyScheduleReadOnlyBlocked]
  );

  const focusedSwapTargetMatchId =
    clickPlacement != null && dragPlacement == null && swapModifierHeld
      ? (focusedSwapTarget?.id ?? null)
      : null;

  return useMemo(
    (): SchedulePlacementController => ({
      keyboardModifierHeld,
      shiftModifierHeld,
      clickPlacement,
      setClickPlacement,
      dragPlacement,
      setDragPlacement,
      pendingPlaceMatchId,
      pendingPlacementSource,
      placementBannerDismissed,
      placementFloatRef,
      scheduleGridWheelRef,
      handlePlacementBannerPointerDownCapture,
      pendingPlaceMatch,
      activePlacementMatch,
      focusedPlacementSlot,
      focusedSwapTargetMatchId,
      swapModifierHeld,
      placementSessionReady,
      placementSessionIndex,
      handlePlaceAtFreeSlot,
      handleSwapPendingWithScheduled,
      handleSelectUnscheduledForPlacement,
      handleSelectGridMatchForPlacement,
      resolveMatchFromDnDData,
    }),
    [
      keyboardModifierHeld,
      shiftModifierHeld,
      clickPlacement,
      dragPlacement,
      pendingPlaceMatchId,
      pendingPlacementSource,
      placementBannerDismissed,
      pendingPlaceMatch,
      activePlacementMatch,
      focusedPlacementSlot,
      focusedSwapTargetMatchId,
      swapModifierHeld,
      placementSessionReady,
      placementSessionIndex,
      handlePlacementBannerPointerDownCapture,
      handlePlaceAtFreeSlot,
      handleSwapPendingWithScheduled,
      handleSelectUnscheduledForPlacement,
      handleSelectGridMatchForPlacement,
      resolveMatchFromDnDData,
    ]
  );
}
